import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { NoteResourceService } from 'src/app/services/HearingWebService/api/noteResource.service';
import { take } from 'rxjs/operators';
import { CommonService } from 'src/app/services/common.service';
import { NoteVO } from 'src/app/services/gen';
import { HearingNoteVO } from 'src/app/services/HearingWebService';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { SharedCodeService } from 'src/app/services/shared-code.service';
import { CONFIRM_DELETE_NOTES, LookUpDataTypes, ScrollStrategy } from 'src/app/shared/common-structures/app.constants';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { AlertService } from 'src/app/shared/ims-alerts/ims-alerts.service';
import { Utils } from '../../../../shared/utility/utils';
import { CaseDetailsService } from '../../case-details/case-details.service';
import { DomainLookupResourceService } from 'src/app/services/HearingWebService';

@Component({
	selector: 'notes-dialog',
	templateUrl: './notes-dialog.component.html',
	styleUrls: ['./notes-dialog.component.scss'],
})
export class NotesDialogComponent implements OnInit, OnChanges {
	@ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
	sort: MatSort;
	pageSizeOptions = [5, 10, 15, 20];
	perPage: number = this.pageSizeOptions[0];
	filter: any = {};
	caseId: number;
	maxLength: number = 16000;
	userdetailId: string;
	dialogTitle: string;
	subjectOptions: any[] = [];
	hasExportPermission: boolean = false;
	isLitigation: boolean = false;
	labelsByCode: any = {};
	displayColumns: string[] = ['icon', 'createdBy', 'posted', 'subject', 'note', 'options'];
	@Input() notes: NoteVO[] | HearingNoteVO[] = [];
	@Input() isAdr: boolean = false;
	notesData: NoteVO[] = [];
	notesToShow: NoteVO[] | HearingNoteVO[] = [];
	noteForm: UntypedFormGroup;
	readOnly: boolean = false;
	isSystemicModuleRoute: boolean;
	currentNote: any = {};
	isNoteSelected = false;
	isHearing: boolean = false;
	public adrOption = { value: 'ADR', display: 'Adr' };
	@Output() onUpdate: EventEmitter<any> = new EventEmitter<any>();
	@Output() subjectChange: EventEmitter<any> = new EventEmitter<any>();
	subjectOptionForComplaintFiling: any = undefined;
	isSubjectOptionsDisabled = false;
	hasEditPermission = false;
	ariaResultAlert: number;

	constructor(
		public dialogRef: MatDialogRef<NotesDialogComponent>,
		private csDtlsServ: CaseDetailsService,
		public cmnServ: CommonService,
		@Inject(NoteResourceService) private noteService: NoteResourceService,
		private sharedCodeServ: SharedCodeService,
		@Inject(DomainLookupResourceService) private domainLookupResourceService: DomainLookupResourceService,
		@Inject(MAT_DIALOG_DATA) public data,
		public dialog: MatDialog,
		private fb: UntypedFormBuilder,
		private router: Router,
		private loader: LoadingService,
		private alert: AlertService,
		private datePipe: DatePipe
	) { }

	ngOnInit() {
		this.dialogTitle = this.data.title;
		this.isHearing = this.data.isHearingCase;
		this.maxLength = this.isHearing ? 1000 : 16000;
		this.caseId = this.data.caseId;
		this.isLitigation = this.data.isLitigation;
		this.readOnly = this.data.readOnly;
		this.hasEditPermission = this.data.hasEditPermission;
		if (this.router.url.includes('/systemic-investigation')) {
			this.isSystemicModuleRoute = true;
		} else {
			this.isSystemicModuleRoute = false;
		}

		this.initializeForm();
		this.cmnServ
			.getUserDetails()
			.pipe(take(1))
			.subscribe((res) => (this.userdetailId = res.userId));
		
		// Load notes based on case type
		if (this.isHearing) {
			this.getHearingNotes();
		} else {
			this.getNotes();
		}
	}

	ngOnChanges(changes): void {
		//Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
		//Add '${implements OnChanges}' to the class.
		if (changes.readOnly && this.readOnly) this.displayColumns = this.displayColumns?.filter((el) => el !== 'options');
	}


	saveHearingNote() {
		this.loader.show();
		this.noteForm.value.isNonDisclosable = this.noteForm.value.isNonDisclosable === true ? 'Y' : 'F';
		const note: HearingNoteVO = this.noteForm.value;
		this.noteService.addNote(this.caseId, note).subscribe(
			(res) => {
				if (res) {
					this.loader.hide();
					this.alert.success('Note created successfully');
					if (!this.labelsByCode[note.subject]) {
						this.getLookupData();
					}
					this.getHearingNotes();
				}
			},
			(error) => {
				this.loader.hide();
				if (error.status === 422) {
					this.alert.error(error?.error, error);
				} else {
					this.alert.error('Failed to get note. Please contact system administrator', error);
				}
			}
		)
	}


	getHearingNotes() {
		this.loader.show();
		this.noteService.getNotes(this.caseId).subscribe(
			(res) => {
				this.loader.hide();
				this.notes = res || [];
				this.onUpdate.emit(this.notes);
				this.onPage({ pageIndex: 0, pageSize: this.perPage });
			},
			(error) => {
				this.loader.hide();
				if (error.status === 422) {
					this.alert.error(error?.error, error);
				} else {
					this.alert.error('Failed to get note. Please contact system administrator', error);
				}
			}
		)
	}
	/**
	 * isChargeParticularsSubject
	 * charge particulars cannot be added or modified or deleted from app
	 */
	public isChargeParticularsSubject = (subjectDescription: string) =>
		subjectDescription?.replace(/ /g, '')?.toUpperCase()?.includes('CHARGEPARTICULARS');
	public isCaseComplaintFilingSubject = (subjectDescription: string) =>
		(this.data?.isLitigation && (this.data?.reasonType?.code === 'TR' || this.data?.reasonType?.code === 'LD')
			&& subjectDescription?.toUpperCase() === 'COMPLAINT FILING') || (!this.data?.isLitigation && subjectDescription?.includes('Legal Disposition'));


	updateLabels() {
		this.labelsByCode = { ADR: 'Adr' };
		this.isSubjectOptionsDisabled = false;
		this.subjectOptions.forEach((x) => {
			this.labelsByCode[x.value] = x.display;
		});
		if (this.data.isLegalRoleHolder && !this.data?.isLitigation && this.data?.readOnly) this.subjectOptions = this.subjectOptions.filter(x => x.value === 'LGL');
		this.handleNewComplaint();

	}

	onSubjectChanges = (value) => {
		this.noteForm.controls['isNonDisclosable'].setValue(
			value ? ['conciliation', 'legal', 'systemic'].includes(value.toLowerCase()) : false
		);
	}

	private handleNewComplaint() {
		const newComplaintFilingSubjects = this.subjectOptions.filter((x) => x.value === 'CFSUMRY') || [];
		if (newComplaintFilingSubjects?.length > 0) {
			newComplaintFilingSubjects.forEach((item: any) => item.isLegalComplaintFiling = true);
		}
	}
	private handleComplaint() {
		const complaintFilingSubjects = this.subjectOptions.filter((x) => x.value === 'CF') || [];
		if (complaintFilingSubjects?.length > 0) {
			complaintFilingSubjects.forEach((item: any) => item.isLegalComplaintFiling = true);
		}
	}

	/**
	 * @description Initialize the form.
	 */
	initializeForm() {
		this.noteForm = this.fb.group({
			subject: new UntypedFormControl(null, [Validators.required, Validators.maxLength(30)]),
			note: new UntypedFormControl(null, [Validators.required, Validators.maxLength(16000)]),
			isNonDisclosable: new UntypedFormControl(null),
		});

		if ((this.data?.status?.code === 'CLOSED' || this.data?.status?.code === 'INQUIRY_CLOSED') && this.data?.readOnly) {
			this.noteForm.disable();
			this.readOnly = true;
		}
	}

	getNotes() {
		this.loader.show();
		this.csDtlsServ.getAllNotes(this.caseId, this.isSystemicModuleRoute, this.isAdr).subscribe(
			(res) => {
				this.loader.hide();
				this.notes = res || [];
				this.onUpdate.emit(this.notes);
				this.onPage({ pageIndex: 0, pageSize: this.perPage });
				if (this.data?.isLitigation && (this.data?.reasonType?.code === 'TR' || this.data?.reasonType?.code === 'LD')) {
					const isComplaintFilingNotesExists = this.notes.filter(note => note.subject.toUpperCase() === 'COMPLAINT FILING') || [];
					if (isComplaintFilingNotesExists?.length > 0 || this.data.complaintFilingDate === undefined) {
						this.subjectOptionForComplaintFiling = this.subjectOptions.filter((x) => x.value === 'CF');
						this.subjectOptionForComplaintFiling.forEach((item: any) => item.isLegalComplaintFiling = true);
					}
				} else {
					this.handleComplaint();
				}

			},
			(error) => {
				this.loader.hide();
				this.alert.error('Failed to get note. Please contact system administrator', error);
			}
		);
	}

	setPageData() {
		this.onPage({ pageIndex: 0, pageSize: this.perPage });
	}

	private isComplianceMonitoring = (): boolean => this.data.status?.code === 'COMPLIANCE_MONITORING';

	getLookupData() {
		let request;
		if (this.isLitigation) {
			request = this.sharedCodeServ.getSharedCodes(LookUpDataTypes.LIT_NOTE_TYPE);
		} else if (this.isHearing) {
			request = this.domainLookupResourceService.getSharedCodeListByDomainName(LookUpDataTypes.HEARING_NOTE);
		} else {
			request = this.sharedCodeServ.getSharedCodes(LookUpDataTypes.NOTETYPE);
		}
		request.subscribe((res) => {
			this.subjectOptions = res.map((x) => {
				return { value: x.code, display: x.description };
			});
			if (!this.isLitigation) {
				this.subjectOptions = this.subjectOptions.filter((item) =>
					this.isComplianceMonitoring() ? item.value === 'COMP' : item.value !== 'COMP'
				);
			}
			this.updateLabels();
			this.subjectChange.emit(this.subjectOptions);
		});
	}
	saveNote() {
		if (this.isSubjectOptionsDisabled === false && this.noteForm.controls['subject'].value.trim().toUpperCase() === 'COMPLAINT FILING' && this.subjectOptionForComplaintFiling && this.subjectOptionForComplaintFiling.length > 0) {
			this.alert.error('Failed to add note, Complaint Filing Note already exists');
			return;
		}

		if (this.data?.reasonType?.code !== 'TR' && this.data?.reasonType?.code !== 'LD' && this.noteForm.controls['subject'].value.trim().toUpperCase() === 'COMPLAINT FILING') {
			this.alert.error('Failed to add note, Complaint Filing not allowed for this case');
			return;
		}

		this.loader.show();
		const note: NoteVO = this.noteForm.value;
		note.noteId = this.currentNote.noteId;
		this.csDtlsServ.saveNote(this.caseId, note, this.isAdr, this.isSystemicModuleRoute).subscribe(
			(res) => {
				this.loader.hide();
				if (!this.labelsByCode[note.subject]) {
					this.getLookupData();
				}
				if (this.isAdr) {
					this.getAdrNotes();
				} else {
					this.getNotes();
				}

				this.clearForm();
				this.currentNote = {};
				this.alert.success('Note created/updated successfully');
			},
			(error) => {
				this.loader.hide();
				if (error.status === 422) {
					this.alert.error(error?.error, error);
				} else {
					this.alert.error('Failed to add/update note. Please contact system administrator', error);
				}
			}
		);
	}
	onDelete(event) {
		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			data: CONFIRM_DELETE_NOTES,
			disableClose: true,
			scrollStrategy: ScrollStrategy,
			id: 'confirm-reopen-allegation-dialog',
		});
		dialogRef.afterClosed().subscribe((res) => {
			if (res) {
				if (this.isHearing) this.onHearingNoteDelete(event);
				else this._delete(event);
				this.clearForm();
				this.currentNote = {};
			}
		});
	}
	_delete(event: NoteVO) {
		this.loader.show();
		this.csDtlsServ.deleteNote(this.caseId, event.noteId, this.isAdr, this.isSystemicModuleRoute).subscribe(
			(res) => {
				this.loader.hide();
				this.notes = res;
				if (this.isAdr) {
					this.getAdrNotes();
				} else {
					this.getNotes();
				}

				this.alert.success('Note deleted successfully.');
			},
			(error) => {
				this.loader.hide();
				if (error.status === 422) {
					this.alert.error(error?.error, error);
				} else {
					this.alert.error('Failed to delete note. Please contact system administrator', error);
				}
			}
		);
	}

	onHearingNoteDelete(event: HearingNoteVO) {
		const noteId = event.hearingNoteId
		this.loader.show();
		this.noteService.deleteNote(this.caseId, noteId).subscribe(
			(res) => {
				this.loader.hide();
				this.notes = res;
				this.getHearingNotes();
				this.alert.success('Note deleted successfully.');
			},
			(error) => {
				this.loader.hide();
				if (error.status === 422) {
					this.alert.error(error?.error, error);
				} else {
					this.alert.error('Failed to delete note. Please contact system administrator', error);
				}
			}
		);

	}

	/**
	 * @description clear the note form
	 */
	clearForm() {
		this.noteForm.reset();
		this.isNoteSelected = false;
	}

	/**
	 * @description Controls the paginator behavior
	 * @param event
	 */
	onPageChange(event) {
		if (this.notes) {
			this.notesToShow = this.notes.slice(
				event.pageIndex * event.pageSize,
				event.pageIndex * event.pageSize + event.pageSize
			);
		} else {
			this.notesToShow = [];
		}
	}

	private convertNotes(notes): NoteVO[] {
		if (this.data.type === 'EMAIL') {
			return notes.map((item) => {
				const rs: NoteVO = {
					subject: item.subject,
					note: item.emailContent,
				};
				return rs;
			});
		} else {
			return notes;
		}
	}

	applyFilter() {
		setTimeout(() => {
			this.onPage({ pageIndex: 0, pageSize: this.perPage });
		}, 100);
	}

	getSorted(sortObj: MatSort) {
		this.sort = sortObj;
		let data = JSON.parse(JSON.stringify(this.notes));
		if (sortObj == null || sortObj == undefined || !sortObj.active) {
			return data;
		}
		let mul = sortObj.direction === 'asc' ? -1 : 1;
		data.sort((a, b) =>
			this.getValue(a, sortObj.active, false) > this.getValue(b, sortObj.active, false) ? mul : -mul
		);
		return data;
	}
	getValue(row, key, asString = true) {
		let value = row;
		key.split('.').forEach((col) => {
			value = value[col];
			if (value == undefined) {
				return '';
			}
		});
		if (value && typeof value == 'string' && new Date(value).toString() != 'Invalid Date') {
			if (asString) {
				value = this.datePipe.transform(value, 'MM/dd/yyyy');
			} else {
				value = new Date(value).getTime();
			}
		}
		return value || '';
	}
	onSort(sort) {
		this.sort = sort;
		this.onPage({ pageIndex: 0, pageSize: this.perPage });
	}
	onPage(data) {
		let allRows = this.getSorted(this.sort);
		let note = (this.filter.note || '').toLowerCase();
		allRows = allRows.filter(
			(x) =>
				(!note ||
					x.note.toLowerCase().includes(note) ||
					x.staffName?.toLowerCase().includes(note) ||
					this.datePipe.transform(x.noteDate, 'MM/dd/yyyy').includes(note) ||
					x.subject.toLowerCase().includes(note)) &&
				(!this.filter.subject || this.filter.subject.length == 0 || this.filter.subject.includes(x.subject))
		);
		this.ariaResultAlert = allRows.length;
		this.notesData = allRows.splice(data.pageIndex * data.pageSize, (data.pageIndex + 1) * data.pageSize);
	}

	onEdit(row) {
		this.currentNote = JSON.parse(JSON.stringify(row));

		if (this.currentNote.subject.toUpperCase() === 'COMPLAINT FILING' && this.subjectOptionForComplaintFiling && this.subjectOptionForComplaintFiling.length > 0) {
			//this.subjectOptionForComplaintFiling.forEach( (item: any) => item.isLegalComplaintFiling = false);
			this.isSubjectOptionsDisabled = true;
		} else {
			this.isSubjectOptionsDisabled = false;
		}
		this.noteForm = this.fb.group({
			subject: new UntypedFormControl(row.subject, [Validators.required]),
			note: new UntypedFormControl(row.note, [Validators.required, Validators.maxLength(16000)]),
			isNonDisclosable: new UntypedFormControl(null),
		});
		this.isNoteSelected = true;
		this.handleNewComplaint();
		//document.getElementById('editForm').scrollIntoView({behavior:"smooth"});
		const dialogContent = document.querySelector('.mat-dialog-content');
		if (dialogContent) {
			dialogContent.scrollTop = 0;
		}
		document.getElementById('note')?.focus();
	}

	onHearingNoteEdit() {
		this.loader.show();
		this.noteForm.value.isNonDisclosable = this.noteForm.value.isNonDisclosable === true ? 'Y' : 'F';
		this.noteForm.value.hearingNoteId = this.currentNote.hearingNoteId;
		const note: HearingNoteVO = this.noteForm.value;
		this.noteService.updateNote(this.caseId, note).subscribe(
			(res) => {
				if (res) {
					this.loader.hide();
					this.alert.success('Note updated successfully');
					this.getHearingNotes();
				}
			},
			(error) => {
				this.loader.hide();
				if (error.status === 422) {
					this.alert.error(error?.error, error);
				} else {
					this.alert.error('Failed to update note. Please contact system administrator', error);
				}
			}
		)


	}

	getAdrNotes() {
		this.loader.show();
		this.csDtlsServ.getADRNotes(this.caseId).subscribe(
			(res) => {
				this.notes = res || [];
				this.onUpdate.emit(this.notes);
				this.onPage({ pageIndex: 0, pageSize: this.perPage });

				this.loader.hide();
			},
			(err) => {
				this.loader.hide();
			}
		);
	}

	public exportNotes() {
		const doc = new jsPDF('l', 'mm', 'a4');
		const head = [['Created By', 'Posted On', 'Subject', 'Notes']];
		const data = [];
		this.notes.forEach((element) => {
			data.push(Object.values(element));
		});
		autoTable(doc, {
			head: head,
			body: data,
			didDrawCell: (data) => { },
		});
		doc.save('Notes.pdf');
		this.logEvents();
	}

	logEvents() {
		const eventData = [
			{
				eventCode: 'CASE_DATA_EXPORTED',
				eventDate: null,
				eventAttributes: [],
				complainantName: null,
				comments: 'Exported case data from Case Notes',
			},
		];
		this.csDtlsServ.addEvents(this.caseId, eventData).subscribe(
			(res) => { },
			(error) => {
				this.alert.error('Error occurred while logging activity', error);
			}
		);
	}

	/**
	 * @description - retrieve the correct human readable value based on a display value passed in
	 * @param value - user selected display value
	 * @param obj - object that is being searched on
	 * @returns - human readable value that is found, or undefined
	 */
	getDescriptionByDisplay(value, obj) {
		if (obj) return obj.find((item) => item.display === value)?.description;
	}

	/**
	 * @description - retrieve the correct human readable list of names based on an array of values passed in
	 * @param value - user selected list of names
	 * @param obj - object that is being searched on
	 * @returns - human readable value that is found, or undefined
	 */
	getNamesByValues(values, obj) {
		if (!Utils.isNullCondition(values) && values.length > 0) {
			let nameList = '';
			values.forEach((value) => (nameList += value + ', '));

			return nameList;
		}
	}
}
